import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { TrendingUp, X, Wand2, Plus, CheckCircle2, AlertTriangle, Circle } from "lucide-react";

const IMPACT = {
  high: { label: "Fort impact", cls: "bg-rose-100 text-rose-700", Icon: AlertTriangle },
  medium: { label: "Impact moyen", cls: "bg-amber-100 text-amber-700", Icon: Circle },
  low: { label: "Impact faible", cls: "bg-slate-100 text-slate-500", Icon: Circle },
};

export default function ConversionModal({ open, onClose, title, metaDescription, currentBlocks, onInsertSections }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const analyze = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cms/ai/conversion", {
        title, meta_description: metaDescription, blocks: currentBlocks,
      });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const insertAll = () => {
    if (!result?.sections?.length) return;
    onInsertSections(result.sections);
    toast.success(`${result.sections.length} section(s) ajoutée(s) au brouillon.`);
    onClose();
  };
  const insertOne = (sec) => { onInsertSections([sec]); toast.success("Section ajoutée au brouillon."); };

  const score = result?.score ?? 0;
  const scoreColor = score >= 70 ? "text-emerald-600" : score >= 40 ? "text-amber-600" : "text-rose-600";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="conversion-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="font-heading font-bold text-slate-900 flex items-center gap-2"><TrendingUp size={18} className="text-[#E05638]" /> Suggestions IA de conversion</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          {!result && (
            <div className="text-center py-6">
              <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">L'IA analyse cette page et propose des améliorations concrètes pour augmenter les inscriptions et les demandes de devis.</p>
              <Button onClick={analyze} disabled={busy} className="bg-[#E05638] text-white" data-testid="conversion-analyze">
                <Wand2 size={16} className="mr-1.5" /> {busy ? "Analyse en cours…" : "Analyser la page"}
              </Button>
            </div>
          )}

          {result && (
            <>
              <div className="flex items-center gap-4 rounded-2xl bg-slate-50 p-4" data-testid="conversion-score">
                <div className={`text-4xl font-extrabold ${scoreColor}`}>{score}<span className="text-lg text-slate-400">/100</span></div>
                <div className="text-sm text-slate-600 flex-1">{result.summary || "Potentiel de conversion estimé de la page."}</div>
              </div>

              {result.priorities?.length > 0 && (
                <div data-testid="conversion-priorities">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Actions prioritaires</div>
                  <div className="space-y-2">
                    {result.priorities.map((p, i) => {
                      const meta = IMPACT[p.impact] || IMPACT.medium;
                      return (
                        <div key={i} className="rounded-xl border border-slate-200 p-3" data-testid={`conversion-priority-${i}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                            <span className="font-semibold text-sm text-slate-800">{p.title}</span>
                          </div>
                          <p className="text-sm text-slate-500">{p.action}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {result.cta_suggestions?.length > 0 && (
                <div data-testid="conversion-cta">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Meilleurs textes de boutons / accroches</div>
                  <div className="space-y-2">
                    {result.cta_suggestions.map((c, i) => (
                      <div key={i} className="rounded-xl border border-slate-200 p-3 text-sm" data-testid={`conversion-cta-${i}`}>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                          <div>
                            <div className="font-semibold text-slate-800">« {c.suggested} »</div>
                            {c.current && c.current !== "—" && <div className="text-xs text-slate-400 line-through">Actuel : {c.current}</div>}
                            {c.why && <div className="text-xs text-slate-500 mt-0.5">{c.why}</div>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.sections?.length > 0 && (
                <div data-testid="conversion-sections">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Sections prêtes à ajouter</div>
                    <Button size="sm" variant="outline" onClick={insertAll} data-testid="conversion-insert-all"><Plus size={14} className="mr-1" /> Tout appliquer</Button>
                  </div>
                  <div className="space-y-2">
                    {result.sections.map((sec, i) => (
                      <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2" data-testid={`conversion-section-${i}`}>
                        <span className="text-sm text-slate-600 truncate">Section suggérée #{i + 1}</span>
                        <Button size="sm" variant="ghost" onClick={() => insertOne(sec)} data-testid={`conversion-insert-${i}`}><Plus size={14} className="mr-1" /> Insérer</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-slate-400">Les ajouts vont dans le brouillon ; rien n'est publié sans votre validation.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
